import React, { useCallback, useRef, useState } from "react";
import BorderHeading from "../../components/heading/BorderHeading";
import "./NewFileUpload.css";
import cameraIcon from "../../images/upload_themeColor6.png";
import { Button, CircularProgress, TextField } from "@mui/material";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import {
  AuthContextType,
  popupType,
  uploadDocumentType,
} from "../../types/types";
import pdfIcon from "../../images/PDF.png";
import pptIcon from "../../images/PPT.png";
import excelIcon from "../../images/XLS.png";
import { EnvironmentRegistry } from "../../services/registry/environmentRegistry";
import axios from "axios";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { TranslationEnum, usePortalTranslation } from "../../providers/TranslationProvider";

const NewFileUpload = () => {
  const { translate } = usePortalTranslation();
  const CUR_COMPONENT_COMMON="commonPortal";
  const ACCEPTED_FILE_TYPES =
    " .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.ms-powerpoint";
  const authContext =
    localStorage.getItem("authContext") &&
    JSON.parse(localStorage.getItem("authContext") as string);

  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [fileUploaded, setFileUploaded] = useState<Blob>();
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [imageIcon, setImageIcon] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const uploadDocument = async (
    formData: FormData,
    authContext: AuthContextType,
    profileName: string
  ): Promise<uploadDocumentType[]> => {
    // setting default values
    const url = EnvironmentRegistry.getUrlFromContext(authContext);
    
    const response = await axios.post(
      `${url}/v1/uploadMultipleFiles/${profileName}`,
      formData,
      {
        headers: EnvironmentRegistry.makeAuthHeadersFromContext(authContext),
      }
    );
    return response.data;
  };

  const fileInputRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const generateBlob = useCallback(async () => {
    if (!fileUploaded) {
      if (isLoading) setIsLoading(false);
      openPopup("Please upload a file", "Alert");
      return;
    }
    const formData = new FormData();
    setIsLoading(true);
    formData.append("files", fileUploaded, selectedFile);
    const fileUploadResponse = await uploadDocument(
      formData,
      authContext,
      "mdm-attendance"
    );
    const blobKey: string = JSON.parse(fileUploadResponse[0].body).features[0]
      .Uri;
    if (blobKey) {
      openPopup("File uploaded Successfully", "Success");
    }
    else{
      openPopup("Something Went wrong","Error");
    }
    setIsLoading(false);
    handleClearAll();
  }, [fileUploaded, selectedFile, authContext]);

  const handleClearAll = useCallback(() => {
    if (isLoading) setIsLoading(false);
    setSelectedFile("");
    setFileUploaded(undefined);
    fileInputRef.current.value = "";
  }, []);

  const handleClick = useCallback(() => {
    if (isLoading) setIsLoading(false);
    fileInputRef.current && fileInputRef.current.click();
  }, []);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) setIsLoading(false);
    const fileUploaded = event.target.files?.[0];
    if (fileUploaded) {
      setFileUploaded(fileUploaded);
      setSelectedFile(fileUploaded.name);
      if (fileUploaded.type === "application/pdf") {
        setImageIcon(pdfIcon);
      } else if (
        fileUploaded.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileUploaded.type === "text/csv" ||
        fileUploaded.type === "application/vnd.ms-excel"
      ) {
        setImageIcon(excelIcon);
      } else if (
        fileUploaded.type ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        fileUploaded.type === "application/vnd.ms-powerpoint"
      ) {
        setImageIcon(pptIcon);
      } else {
        setImageIcon(URL.createObjectURL(fileUploaded));
      }
    }
  };

  return (
    <div className="file_upload_container">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <BorderHeading>Absenteeism Data Upload</BorderHeading>
      <div className="file_upload_div" onClick={handleClick}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileUpload}
          accept={ACCEPTED_FILE_TYPES}
        />
        {fileUploaded ? (
          <div className="image">
            <img
              width={60}
              height={65}
              src={imageIcon}
              alt={translate(TranslationEnum.common_portal,"Image not available")}
            />
            {selectedFile}
          </div>
        ) : (
          <div className="icon_and_text">
            <RenderSvg name="UploadImportButton" />
            {translate(TranslationEnum.common_portal,"Upload csv, xlsx")}
          </div>
        )}
      </div>

      <div className="buttons">
        <Button
          variant="contained"
          sx={{ backgroundColor: "#23c6b0" }}
          onClick={generateBlob}
        >
          {isLoading ? <CircularProgress size={24} /> : translate(CUR_COMPONENT_COMMON,"Upload")}
        </Button>
      </div>
    </div>
  );
};
export default NewFileUpload;
